import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, MoveRight, LogOut } from "lucide-react";
import { StatusPill, NoCoordBadge, InServerBadge } from "@/components/network/StatusPill";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { KodeHistory } from "@/components/network/KodeHistory";
import { ArchiveInfraButton } from "@/components/network/ArchiveInfraButton";
import { AddMemberDialog, MoveMemberDialog, RemoveMemberDialog, MEMBER_KINDS } from "@/components/network/ServerMembers";

function MemberRow({ m, srv, canManage, onSelect, onMove, onRemove }) {
  const t = `${m.kind}-${m.kode}`;
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded bg-[#F8F9FA] p-1.5 text-xs" data-testid={`server-member-${t}`}>
      <div className="min-w-0">
        {onSelect ? <button className="break-all font-mono-tab font-semibold text-[#0055FF] hover:underline" onClick={() => onSelect({ kind: m.kind, id: m.id })} data-testid={`server-member-open-${t}`}>{m.kode}</button>
          : <span className="break-all font-mono-tab font-semibold">{m.kode}</span>}
        {m.archived && <span className="ml-1 rounded bg-[#E5E7EB] px-1 text-[10px] font-semibold text-[#374151]" data-testid={`server-member-archived-${t}`}>ARSIP</span>}
        <span className="text-[#6B7280]"> · {m.nama}</span>
        <div className="mt-0.5 flex flex-wrap items-center gap-1"><StatusPill status={m.status} /><InServerBadge s={srv} testid={`server-member-loc-${t}`} /></div>
      </div>
      {canManage && <div className="flex shrink-0">
        <Button size="icon" variant="ghost" className="h-7 w-7" title="Pindahkan" onClick={() => onMove(m)} data-testid={`server-member-move-${t}`}><MoveRight className="h-3.5 w-3.5" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" title="Keluarkan" onClick={() => onRemove(m)} data-testid={`server-member-remove-${t}`}><LogOut className="h-3.5 w-3.5 text-[#B91C1C]" /></Button>
      </div>}
    </li>
  );
}

// Detail SERVER: info + daftar anggota (OLT/OTB/Splitter) + kelola keanggotaan (Admin/Koordinator).
export function ServerDetail({ serverId, me, wilayahName, lokasiName, onSelect, onArchived, onChanged, reloadKey, extra }) {
  const [d, setD] = useState(null);
  const [k, setK] = useState(0);
  const [add, setAdd] = useState(false);
  const [mv, setMv] = useState(null);
  const [rm, setRm] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/network/servers/${serverId}`).then((r) => setD(r.data)).catch(() => toast.error("Gagal memuat detail SERVER"));
  }, [serverId, reloadKey, k]);
  if (!d) return <p className="text-xs" data-testid="server-detail-loading">Memuat…</p>;
  const canManage = !!me?.can_manage;
  const done = () => { setAdd(false); setMv(null); setRm(null); setK((x) => x + 1); onChanged?.(); };
  return (
    <div className="space-y-3 text-sm" data-testid="server-detail">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><div className="break-all font-display text-xl font-bold" data-testid="server-detail-kode">{d.kode}</div>
          <div className="text-xs text-[#6B7280]">{d.nama} · {wilayahName(d.wilayah_id)}{lokasiName ? ` · ${lokasiName(d.lokasi_id)}` : ""}</div></div>
        <ArchiveInfraButton kind="server" item={d} me={me} onDone={onArchived || done} />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {d.archived && <span className="rounded bg-[#E5E7EB] px-1.5 py-0.5 font-semibold text-[#374151]" data-testid="server-detail-archived">ARSIP · {d.archived_by} · {(d.archived_at || "").slice(0, 10)}</span>}
        <StatusPill status={d.status} /><span className="rounded bg-[#F1F5F9] px-1.5 py-0.5 font-semibold text-[#334155]" data-testid="server-detail-count">{d.member_count} anggota</span>
        {!d.has_coords && <NoCoordBadge testid="server-detail-nocoord" />}
        <ArahkanButton lat={d.latitude} lng={d.longitude} testid="server-nav" />
      </div>
      {d.alamat && <p className="text-xs">{d.alamat}</p>}
      {extra}
      <section data-testid="server-members">
        <div className="flex items-center justify-between"><b className="text-xs uppercase text-[#6B7280]">Perangkat Anggota ({d.member_count})</b>
          {canManage && !d.archived && <Button size="sm" variant="outline" onClick={() => setAdd(true)} data-testid="server-add-member-btn"><Plus className="mr-1 h-3.5 w-3.5" />Tambah Anggota</Button>}</div>
        {MEMBER_KINDS.map(([kind, label]) => {
          const ms = d.members.filter((m) => m.kind === kind);
          return ms.length > 0 && <div key={kind} className="mt-2" data-testid={`server-members-${kind}`}><div className="mb-1 text-[11px] font-semibold text-[#6B7280]">{label} ({ms.length})</div>
            <ul className="space-y-1">{ms.map((m) => <MemberRow key={m.id} m={m} srv={{ kode: d.kode, has_coords: d.has_coords, label: "SERVER" }} canManage={canManage} onSelect={onSelect} onMove={setMv} onRemove={setRm} />)}</ul></div>;
        })}
        {d.members.length > 0 && <p className="mt-1 text-[11px] text-[#6B7280]" data-testid="server-members-loc-note">
          Lokasi anggota mengikuti SERVER ini dan tidak ditampilkan sebagai titik terpisah di peta.</p>}
        {!d.members.length && <p className="mt-1 text-xs text-[#6B7280]" data-testid="server-no-member">Belum ada perangkat anggota.</p>}
      </section>
      {d.catatan && <p className="text-xs text-[#6B7280]">Catatan: {d.catatan}</p>}
      <p className="text-[11px] text-[#6B7280]">Diubah {d.updated_by} · {(d.updated_at || "").slice(0, 10)}</p>
      <KodeHistory kind="server" id={d.id} kode={d.kode} />
      {canManage && <>
        <AddMemberDialog open={add} server={d} onClose={() => setAdd(false)} onDone={done} />
        <MoveMemberDialog member={mv} server={d} onClose={() => setMv(null)} onDone={done} />
        <RemoveMemberDialog member={rm} server={d} onClose={() => setRm(null)} onDone={done} />
      </>}
    </div>
  );
}
